import { useEffect } from "react";
import { capture } from "./analytics";

/**
 * Catalogue unique des événements métier.
 *
 * Toute instrumentation passe par une constante d'ici : c'est ce qui garantit
 * qu'une même action ne finit jamais sous deux noms. Une faute de frappe dans
 * un littéral passerait inaperçue jusqu'au tableau de bord, une constante
 * inexistante casse au build.
 *
 * Convention : snake_case, objet puis action au passé (`watch_registered`).
 * `login` / `logout` dérogent volontairement : ce sont les noms attendus par
 * les entonnoirs d'authentification de PostHog.
 *
 * Un nom déjà en production ne se renomme pas à la légère : PostHog n'agrège
 * pas l'ancien et le nouveau. Voir REGISTER_CTA_CLICKED et WATCH_REGISTERED.
 */
export const EVENTS = Object.freeze({
  // Consentement
  //
  // Pas de `consent_denied` : au moment du refus, PostHog est encore désactivé
  // et capture() ne fait rien. Contourner cela reviendrait à mesurer quelqu'un
  // qui vient précisément de refuser d'être mesuré. Le taux d'acceptation se
  // calcule contre une source qui ne dépose rien (journaux serveur,
  // Cloudflare), jamais en traçant les refus.
  // `consent_revoked` est en revanche mesurable : la personne avait consenti,
  // la capture est encore active à l'instant du retrait, et l'événement porte
  // la fin d'un consentement donné, pas le refus de quelqu'un qui n'a jamais dit oui.
  CONSENT_GRANTED: "consent_granted",
  CONSENT_REVOKED: "consent_revoked",

  // Navigation
  REGISTER_CTA_CLICKED: "register_cta_clicked",
  HOW_IT_WORKS_VIEWED: "how_it_works_viewed",
  PRICING_VIEWED: "pricing_viewed",
  FAQ_VIEWED: "faq_viewed",
  SIGNATORY_CLICKED: "signatory_clicked",
  CONTACT_CLICKED: "contact_clicked",

  // Inscription d'une montre
  REGISTER_STARTED: "register_started",
  REGISTER_STEP_1_COMPLETED: "register_step_1_completed",
  REGISTER_STEP_2_COMPLETED: "register_step_2_completed",
  // Distinct de register_step_2_completed, qui vaut pour tout le monde :
  // celui-ci ne concerne que les visiteurs sans compte, arrêtés par le mur.
  // Le rapport entre les deux est le taux de blocage.
  LOGIN_WALL_HIT: "login_wall_hit",
  // Un dossier ouvert reste un dossier ouvert, quelle que soit la porte : le
  // formulaire public, l'atelier d'un créateur ou l'enregistrement pour un
  // client depuis le portail. Un seul nom, donc un seul chiffre à lire, et la
  // propriété `channel` pour le découper. Les événements antérieurs au portail
  // ne la portent pas : leur absence signifie `public_form`.
  WATCH_REGISTERED: "watch_registered",
  REGISTRATION_FAILED: "registration_failed",

  // Portail professionnel
  //
  // L'écran porteur de la bêta n'émettait rien : un artisan pouvait ouvrir le
  // portail, hésiter et repartir sans que rien ne le dise. Ces deux noms
  // complètent l'entonnoir que `$pageview` sur /pro commence :
  // arrivée → formulaire ouvert → dossier ouvert → provenance consignée.
  // `pro_lookup` est l'entrée unique : le professionnel donne un numéro, le
  // registre répond. Sa propriété `found` sépare les deux suites — enrichir un
  // dossier, ou en ouvrir un — là où le portail laissait auparavant le
  // professionnel choisir la porte avant de savoir. Jamais le numéro cherché.
  PRO_LOOKUP: "pro_lookup",
  PRO_REGISTER_STARTED: "pro_register_started",
  PRO_EVENT_RECORDED: "pro_event_recorded",

  // Compte
  ACCOUNT_CREATED: "account_created",
  LOGIN: "login",
  LOGOUT: "logout",

  // Lecture du numéro de série par photo
  OCR_OPENED: "ocr_opened",
  OCR_SUCCESS: "ocr_success",
  OCR_FAILURE: "ocr_failure",
  // Lire n'est pas servir : seul ce choix prouve que la proposition était juste.
  SERIAL_SCAN_CANDIDATE_PICKED: "serial_scan_candidate_picked",

  // Reprise d'un dossier créé pour soi par un tiers
  CLAIM_STARTED: "claim_started",
  CLAIM_COMPLETED: "claim_completed",

  // Import en masse (marchands)
  BULK_IMPORT_STARTED: "bulk_import_started",
  BULK_IMPORT_VALIDATED: "bulk_import_validated",
  BULK_IMPORT_COMPLETED: "bulk_import_completed",

  // Consultation du registre
  SERIAL_SEARCH: "serial_search",
  SERIAL_FOUND: "serial_found",
  SERIAL_NOT_FOUND: "serial_not_found",

  // Signalement de vol
  THEFT_REPORTED: "theft_reported",

  // Passeports
  PASSPORT_VIEWED: "passport_viewed",
  PASSPORT_SHARED: "passport_shared",

  // Dossier public
  //
  // La seule page qui s'adresse à quelqu'un sans compte : l'acheteur devant la
  // montre, le curieux qui scanne. C'est là que le registre prouve — ou ne
  // prouve pas — qu'il sert à quelque chose, et elle n'émettait rien.
  //
  // Les propriétés décrivent ce que le visiteur a eu SOUS LES YEUX, pas le
  // dossier en soi : un dossier corroboré par un signataire et un dossier nu
  // ne convainquent pas de la même façon, et c'est cet écart qu'on veut lire.
  // Ni identifiant de dossier ni numéro de série : la marque et le modèle sont
  // publics sur cette page, l'identité de la pièce ne l'est pas.
  DOSSIER_VIEWED: "dossier_viewed",
  // Distinct, comme serial_found l'est de serial_not_found : quelqu'un qui
  // scanne un dossier inexistant n'a pas consulté le registre, il s'est heurté
  // à lui.
  DOSSIER_NOT_FOUND: "dossier_not_found",
  // Le seul geste que la page propose : « cette montre est à moi ». C'est le
  // passage du public au registre, donc la conversion qui compte ici.
  DOSSIER_ENRICH_CLICKED: "dossier_enrich_clicked",
  // Passeport métal : l'arrivée par la puce, avant même le dossier. Sans lui,
  // une visite venue d'un objet posé sur une table est indiscernable d'un lien
  // ouvert dans un navigateur.
  NFC_TAPPED: "nfc_tapped",

  // Pièces jointes
  PHOTO_UPLOADED: "photo_uploaded",
  INVOICE_UPLOADED: "invoice_uploaded",
  // Tout justificatif qui n'est pas la facture. `invoice_uploaded` garde son
  // nom et son historique ; les séparer permet de voir si les propriétaires
  // s'arrêtent à la preuve d'achat ou documentent aussi l'entretien.
  DOCUMENT_UPLOADED: "document_uploaded",
});

/**
 * mobile | tablet | desktop, d'après la largeur d'affichage.
 *
 * Volontairement basé sur le viewport et non sur le user-agent : c'est la
 * largeur qui décide de la mise en page servie, donc de ce que la personne a
 * réellement vu. Les seuils suivent ceux de Tailwind utilisés dans l'interface.
 */
export const deviceType = () => {
  if (typeof window === "undefined") return undefined;
  const width = window.innerWidth;
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
};

// Provenance de la visite : le chemin interne d'où l'on arrive, sinon le
// domaine externe, sinon "direct". Répond à « d'où viennent les inscriptions ».
const trafficSource = () => {
  if (typeof document === "undefined") return undefined;
  const ref = document.referrer;
  if (!ref) return "direct";
  try {
    const url = new URL(ref);
    if (url.origin === window.location.origin) return url.pathname;
    return url.hostname;
  } catch {
    return "external";
  }
};

/**
 * Classe un délai en tranches plutôt qu'en jours exacts.
 *
 * Un nombre de jours précis, associé à une marque et à l'horodatage de
 * l'événement, permettrait de remonter à un signalement particulier. La
 * tranche répond à la seule question utile (« signale-t-on vite ? ») sans
 * rendre personne reconnaissable.
 */
export const delayBucket = (fromIso, now = Date.now()) => {
  if (!fromIso) return undefined;
  const then = new Date(fromIso).getTime();
  if (Number.isNaN(then)) return undefined;
  const days = Math.floor((now - then) / 86400000);
  if (days < 0) return "future_date";
  if (days === 0) return "same_day";
  if (days <= 7) return "within_week";
  if (days <= 30) return "within_month";
  if (days <= 365) return "within_year";
  return "over_a_year";
};

/**
 * Vrai quand la page a été ouverte depuis un QR code Provena.
 *
 * Les QR gravés sur les passeports encodent `/verify/{id}?t=<jeton signé>` :
 * la présence de ce jeton est le seul signal fiable d'une arrivée par scan,
 * un lien copié-collé ne le porte pas.
 */
export const fromQrCode = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("t");
};

/**
 * Contexte d'entrée, à joindre aux événements de début de parcours.
 *
 * PostHog attache déjà les UTM au $pageview et à la personne, mais les répéter
 * ici rend l'événement lisible seul, sans jointure, dans un entonnoir.
 * Les clés vides sont omises pour ne pas polluer le schéma des propriétés.
 */
export const entryContext = () => {
  const params =
    typeof window === "undefined"
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const context = {
    source: trafficSource(),
    device_type: deviceType(),
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
  };
  Object.keys(context).forEach((k) => {
    if (context[k] === undefined) delete context[k];
  });
  return context;
};

/**
 * Émet un événement au plus une fois par onglet.
 *
 * Nécessaire pour `login`, qui est déduit de la résolution de session : celle-ci
 * rejoue à chaque rechargement de page, alors que la connexion, elle, n'a eu
 * lieu qu'une fois.
 */
export const captureOnce = (event, properties) => {
  const key = `provena_evt_${event}`;
  try {
    if (sessionStorage.getItem(key)) return;
  } catch {
    /* sessionStorage indisponible : on émet, quitte à doublonner */
  }
  // Le verrou n'est posé que si l'événement est réellement parti. Avant le
  // consentement, capture() ne fait rien : verrouiller à ce moment-là
  // condamnerait l'événement pour toute la session, alors que la personne
  // accepte souvent la bannière quelques secondes plus tard.
  if (!capture(event, properties)) return;
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* sessionStorage indisponible */
  }
};

// Oublie les verrous de captureOnce (à la déconnexion) pour que la connexion
// suivante, dans le même onglet, soit bien comptée.
export const clearCaptureOnce = () => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("provena_evt_"))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* sessionStorage indisponible */
  }
};

/**
 * Émet un événement de consultation une fois par montage de page.
 *
 * `properties` est volontairement hors dépendances : le tableau que lui passe
 * l'appelant est recréé à chaque rendu et relancerait l'effet en boucle. Seul
 * le nom d'événement pilote le déclenchement.
 */
export const useTrackView = (event, properties) => {
  useEffect(() => {
    capture(event, properties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
};
